/**
 * Full Companies House MR01 — Particulars of a charge.
 *
 * Mirrors the official MR01 v3.0 final form layout exactly:
 *   Form header — legal reference (s859A & s859J CA 2006), 21-day deadline.
 *   1. Company details
 *   2. Charge creation date
 *   3. Names of persons entitled (repeater; flag if >4)
 *   4. Brief description of property (fixed charge)
 *   5. Other charge or fixed security over property NOT described above? Y/N (+ description)
 *   6. Floating charge — Y/N; if Y, covers all property/undertaking? Y/N (+ description)
 *   7. Negative pledge — Y/N
 *   8. Trustee statement — tick if company is acting as trustee
 *   Authentication — printed name (no signature required)
 *   Attachments — certified copy of instrument enclosed
 *
 * Companies House MR01 has no API — we capture the data, generate a fully
 * populated PDF, and (when filing) the Companies House WebFiling URL is
 * already linked via the Companies House live adapter using the Presenter
 * ID + Auth Code from the firm-level Submission Settings.
 */
import { useState, useEffect, useRef } from "react";
import { TextField, NumberField, DateField, SelectField, YesNoField, TextAreaField, PanelGrid } from "./FormPrimitives";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Warning, Paperclip, CheckCircle, X } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";

function emptyChargee() {
  return { name: "", address_1: "", postcode: "", country: "United Kingdom" };
}

export function defaultsMr01(caseData, item) {
  const m = item?.metadata || {};
  const corpClient = (caseData?.clients || []).find((c) => c.is_company);
  return {
    // Section 1
    company_number: m.company_number || caseData?.companies_house_number || corpClient?.companies_house_number || "",
    company_name: m.company_name || caseData?.company_name || corpClient?.name || corpClient?.company_name || "",
    company_registered_office: m.company_registered_office || corpClient?.registered_office || corpClient?.address || "",
    // Section 2 — Charge creation date
    charge_creation_date: m.charge_creation_date || caseData?.target_completion_date || todayIso(),
    instrument_date: m.instrument_date || caseData?.target_completion_date || todayIso(),
    // Section 3 — chargees
    chargees: m.chargees?.length ? m.chargees : [{
      name: caseData?.lender_name || "",
      address_1: "",
      postcode: "",
      country: "United Kingdom",
    }],
    more_than_four_chargees: m.more_than_four_chargees || "no",
    // Section 4 — fixed charge property description
    property_charged_description: m.property_charged_description || `Freehold/leasehold property at ${caseData?.property_address || ""}`,
    refers_to_instrument: m.refers_to_instrument || "no",
    // Section 5 — other charges
    other_fixed_charge: m.other_fixed_charge || "no",
    other_fixed_charge_description: m.other_fixed_charge_description || "",
    // Section 6 — floating charge
    floating_charge: m.floating_charge || "no",
    floating_covers_all: m.floating_covers_all || "yes",
    floating_charge_description: m.floating_charge_description || "",
    // Section 7 — negative pledge
    negative_pledge: m.negative_pledge || "no",
    // Section 8 — trustee
    company_is_trustee: m.company_is_trustee || "no",
    // Optional / SDLT-style metadata
    charge_code: m.charge_code || "",
    amount_secured: m.amount_secured || caseData?.mortgage_amount || "",
    amount_secured_words: m.amount_secured_words || "",
    // Authentication
    authenticator_name: m.authenticator_name || caseData?.solicitor_name || "",
    authenticator_capacity: m.authenticator_capacity || "Solicitor",
    // Attachments
    instrument_certified_copy_enclosed: m.instrument_certified_copy_enclosed || "yes",
    filing_method: m.filing_method || "online",
    deed_document_id: m.deed_document_id || "",
    deed_filename: m.deed_filename || "",
  };
}

export function validateMr01(v) {
  const errs = {};
  if (!v.company_number) errs.company_number = "Required (Section 1)";
  if (!v.company_name) errs.company_name = "Required (Section 1)";
  if (!v.charge_creation_date) errs.charge_creation_date = "Required (Section 2)";
  if (!(v.chargees || []).length || !v.chargees[0]?.name) errs.chargees = "At least 1 chargee required (Section 3)";
  if (!v.property_charged_description) errs.property_charged_description = "Required (Section 4)";
  if (v.other_fixed_charge === "yes" && !v.other_fixed_charge_description) errs.other_fixed_charge_description = "Describe the other fixed charge (Section 5)";
  if (v.floating_charge === "yes" && v.floating_covers_all === "no" && !v.floating_charge_description) errs.floating_charge_description = "Describe what the floating charge covers (Section 6)";
  if (!v.authenticator_name) errs.authenticator_name = "Authenticator name required";
  if (!v.deed_document_id) errs.deed_document_id = "Attach the certified copy of the signed deed before filing";
  return errs;
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

// ---- Repeaters ----------------------------------------------------------

function ChargeeEditor({ c, idx, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/40" data-testid={`chargee-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Chargee #{idx + 1}</div>
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 h-7 px-2" data-testid={`chargee-remove-${idx}`}>
            <Trash weight="bold" className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <PanelGrid>
        <TextField name={`chargee_${idx}_name`} label="Chargee / lender name" required value={c.name} onChange={(x) => set("name", x)} span={3} />
        <TextField name={`chargee_${idx}_address_1`} label="Address" value={c.address_1} onChange={(x) => set("address_1", x)} span={2} />
        <TextField name={`chargee_${idx}_postcode`} label="Postcode" value={c.postcode} onChange={(x) => set("postcode", x)} />
        <TextField name={`chargee_${idx}_country`} label="Country" value={c.country} onChange={(x) => set("country", x)} />
      </PanelGrid>
    </div>
  );
}

// ---- Panels --------------------------------------------------------------

export function Mr01DeadlineNotice() {
  return (
    <div className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="mr01-deadline-notice">
      <Warning weight="fill" className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <strong>21-day statutory deadline.</strong> Under s859A &amp; s859J Companies Act 2006, this MR01 must be delivered to
        Companies House within <strong>21 days</strong> beginning with the day after the date of creation of the charge — or
        the charge will be void against an administrator / liquidator. Online filing fee £14, paper fee £24.
      </div>
    </div>
  );
}

export function Mr01PanelCompany({ v, set, e }) {
  return (
    <PanelGrid>
      <TextField name="company_number" label="Company number" required hint="8-digit Companies House number" value={v.company_number} onChange={(x) => set("company_number", x)} error={e.company_number} />
      <TextField name="company_name" label="Company name (in full)" required value={v.company_name} onChange={(x) => set("company_name", x)} error={e.company_name} span={2} />
      <TextAreaField name="company_registered_office" label="Registered office address" rows={2} value={v.company_registered_office} onChange={(x) => set("company_registered_office", x)} />
    </PanelGrid>
  );
}

export function Mr01PanelCreationDate({ v, set, e }) {
  return (
    <PanelGrid>
      <DateField name="charge_creation_date" label="Date charge was created" required hint="Day / Month / Year — usually completion" value={v.charge_creation_date} onChange={(x) => set("charge_creation_date", x)} error={e.charge_creation_date} />
      <DateField name="instrument_date" label="Date of charge instrument" hint="If different from creation date" value={v.instrument_date} onChange={(x) => set("instrument_date", x)} />
      <TextField name="charge_code" label="Charge code" hint="Allocated by Companies House after filing" value={v.charge_code} onChange={(x) => set("charge_code", x)} />
      <NumberField name="amount_secured" label="Amount secured (£)" hint="Optional — for internal tracking" value={v.amount_secured} onChange={(x) => set("amount_secured", x)} />
      <TextField name="amount_secured_words" label="Amount in words" hint="e.g. Two hundred and fifty thousand pounds" value={v.amount_secured_words} onChange={(x) => set("amount_secured_words", x)} span={2} />
    </PanelGrid>
  );
}

export function Mr01PanelChargees({ v, set, e }) {
  const list = v.chargees || [];
  const update = (i, c) => set("chargees", list.map((p, idx) => idx === i ? c : p));
  const remove = (i) => set("chargees", list.filter((_, idx) => idx !== i));
  const add = () => set("chargees", [...list, emptyChargee()]);
  return (
    <div className="space-y-3 py-3" data-testid="mr01-chargees-panel">
      <p className="text-xs text-slate-500">
        Names of persons, security agents or trustees entitled to the charge. If more than 4, tick the &ldquo;more than 4 names&rdquo; flag below.
      </p>
      {list.map((c, i) => (
        <ChargeeEditor key={i} c={c} idx={i} onChange={(nc) => update(i, nc)} onRemove={list.length > 1 ? () => remove(i) : null} />
      ))}
      <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="mr01-add-chargee">
        <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another chargee
      </Button>
      <div className="pt-2 border-t border-slate-100">
        <YesNoField name="more_than_four_chargees" label="More than 4 chargees in total?" hint="Tick if the list above is not exhaustive — extra names go on a continuation sheet" value={v.more_than_four_chargees} onChange={(x) => set("more_than_four_chargees", x)} />
      </div>
      {e.chargees && <p className="text-[11px] text-rose-600">{e.chargees}</p>}
    </div>
  );
}

export function Mr01PanelFixedCharge({ v, set, e }) {
  return (
    <PanelGrid>
      <TextAreaField
        name="property_charged_description"
        label="Brief description of property (fixed charge)"
        rows={4}
        required
        hint="Short description of land (incl. buildings), ship, aircraft or registered IP subject to a fixed charge. Multiple plots — describe some + add 'for more details please refer to the instrument'."
        value={v.property_charged_description}
        onChange={(x) => set("property_charged_description", x)}
        error={e.property_charged_description}
      />
      <YesNoField name="refers_to_instrument" label="Refers reader to the instrument for more detail?" value={v.refers_to_instrument} onChange={(x) => set("refers_to_instrument", x)} />
    </PanelGrid>
  );
}

export function Mr01PanelOtherCharge({ v, set, e }) {
  return (
    <PanelGrid>
      <YesNoField name="other_fixed_charge" label="Does the instrument include a fixed charge / fixed security over any property NOT described in Section 4?" value={v.other_fixed_charge} onChange={(x) => set("other_fixed_charge", x)} span={3} />
      {v.other_fixed_charge === "yes" && (
        <TextAreaField name="other_fixed_charge_description" label="Description of other fixed charge property" required rows={3} value={v.other_fixed_charge_description} onChange={(x) => set("other_fixed_charge_description", x)} error={e.other_fixed_charge_description} />
      )}
    </PanelGrid>
  );
}

export function Mr01PanelFloatingCharge({ v, set, e }) {
  return (
    <PanelGrid>
      <YesNoField name="floating_charge" label="Is the instrument expressed to contain a floating charge?" value={v.floating_charge} onChange={(x) => set("floating_charge", x)} span={3} />
      {v.floating_charge === "yes" && (
        <>
          <YesNoField name="floating_covers_all" label="Does the floating charge cover ALL the property and undertaking of the company?" value={v.floating_covers_all} onChange={(x) => set("floating_covers_all", x)} span={3} />
          {v.floating_covers_all === "no" && (
            <TextAreaField name="floating_charge_description" label="Description of what the floating charge covers" required rows={3} value={v.floating_charge_description} onChange={(x) => set("floating_charge_description", x)} error={e.floating_charge_description} />
          )}
        </>
      )}
    </PanelGrid>
  );
}

export function Mr01PanelNegativePledge({ v, set }) {
  return (
    <PanelGrid>
      <YesNoField
        name="negative_pledge"
        label="Do any terms of the charge prohibit or restrict the company from creating further security ranking equally with or ahead of this charge? (Negative pledge)"
        value={v.negative_pledge}
        onChange={(x) => set("negative_pledge", x)}
        span={3}
      />
    </PanelGrid>
  );
}

export function Mr01PanelTrustee({ v, set }) {
  return (
    <PanelGrid>
      <YesNoField
        name="company_is_trustee"
        label="Is the company in Section 1 acting as TRUSTEE of the property / undertaking which is the subject of the charge?"
        hint="Tick yes only if applicable — common for SPV / trust structures"
        value={v.company_is_trustee}
        onChange={(x) => set("company_is_trustee", x)}
        span={3}
      />
    </PanelGrid>
  );
}

export function Mr01PanelAuthentication({ v, set, e }) {
  return (
    <PanelGrid>
      <TextField name="authenticator_name" label="Printed name" required hint="No signature required — this name appears on the public record" value={v.authenticator_name} onChange={(x) => set("authenticator_name", x)} error={e.authenticator_name} span={2} />
      <TextField name="authenticator_capacity" label="Capacity / interest in the charge" value={v.authenticator_capacity} onChange={(x) => set("authenticator_capacity", x)} hint="e.g. Solicitor for the chargee" />
    </PanelGrid>
  );
}

export function Mr01PanelAttachment({ v, set, e, caseId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = async () => {
    if (!caseId) return;
    try {
      const { data } = await API.get(`/cases/${caseId}/documents`);
      const pdfs = (data || []).filter(
        (d) =>
          (d.content_type || "").includes("pdf") ||
          (d.original_filename || "").toLowerCase().endsWith(".pdf"),
      );
      setDocs(pdfs);
    } catch (err) {
      toast.error("Couldn't load case documents");
    }
  };
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [caseId]);

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && !(file.type || "").includes("pdf")) {
      toast.error("The deed must be a PDF.");
      return;
    }
    setUploading(true);
    const tid = toast.loading("Uploading executed deed…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/cases/${caseId}/documents?category=charge_deed`, fd);
      await loadDocs();
      set("deed_document_id", data.id);
      set("deed_filename", data.original_filename);
      toast.success("Deed uploaded & attached", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { id: tid });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const selected = docs.find((d) => d.id === v.deed_document_id);

  return (
    <div className="space-y-3 py-3" data-testid="mr01-attachment-panel">
      <p className="text-[12px] text-slate-600 leading-snug">
        Companies House requires a <strong>certified copy of the signed charge instrument</strong> (the
        executed mortgage deed) to be filed with the MR01. Pick it from the case documents or upload it now.
      </p>

      <div className="space-y-1">
        <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
          Attach from case documents
        </label>
        <select
          value={v.deed_document_id || ""}
          onChange={(ev) => {
            const d = docs.find((x) => x.id === ev.target.value);
            set("deed_document_id", ev.target.value || "");
            set("deed_filename", d?.original_filename || "");
          }}
          className="h-9 text-sm rounded-md border border-slate-200 bg-white px-3 w-full"
          data-testid="mr01-deed-select"
        >
          <option value="">— Select a PDF from this case —</option>
          {docs.map((d) => (
            <option key={d.id} value={d.id}>{d.original_filename}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">or</span>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onUpload}
          className="hidden"
          data-testid="mr01-deed-upload-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading || !caseId}
          onClick={() => fileRef.current?.click()}
          data-testid="mr01-deed-upload-btn"
        >
          <Paperclip weight="bold" className="w-4 h-4 mr-1" />
          {uploading ? "Uploading…" : "Upload executed deed"}
        </Button>
      </div>

      {selected ? (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
          data-testid="mr01-deed-attached"
        >
          <span className="flex items-center gap-2 text-[12.5px] text-emerald-800 truncate">
            <CheckCircle weight="fill" className="w-4 h-4 shrink-0 text-emerald-600" />
            <span className="truncate">{selected.original_filename}</span>
          </span>
          <button
            type="button"
            onClick={() => { set("deed_document_id", ""); set("deed_filename", ""); }}
            className="text-rose-600 hover:text-rose-700 shrink-0"
            data-testid="mr01-deed-remove"
            aria-label="Remove attached deed"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-amber-600 flex items-center gap-1" data-testid="mr01-deed-missing">
          <Warning weight="fill" className="w-3.5 h-3.5" />
          No deed attached yet — required before filing.
        </p>
      )}
      {e?.deed_document_id && <p className="text-[11px] text-rose-600">{e.deed_document_id}</p>}
    </div>
  );
}

export function mr01Panels() {
  return [
    { id: "deadline", label: "Statutory deadline (21 days)", Component: ({ v, set, e }) => <Mr01DeadlineNotice />, fields: [], requiredFields: [] },
    { id: "company", label: "1. Company details", Component: Mr01PanelCompany, fields: ["company_number", "company_name"], requiredFields: ["company_number", "company_name"] },
    { id: "creation_date", label: "2. Charge creation date", Component: Mr01PanelCreationDate, fields: ["charge_creation_date"], requiredFields: ["charge_creation_date"] },
    { id: "chargees", label: "3. Names of persons entitled", Component: Mr01PanelChargees, fields: ["chargees"], requiredFields: ["chargees"] },
    { id: "fixed_charge", label: "4. Brief description (fixed charge property)", Component: Mr01PanelFixedCharge, fields: ["property_charged_description"], requiredFields: ["property_charged_description"] },
    { id: "other_charge", label: "5. Other charge / fixed security", Component: Mr01PanelOtherCharge, fields: ["other_fixed_charge"], requiredFields: ["other_fixed_charge"] },
    { id: "floating_charge", label: "6. Floating charge", Component: Mr01PanelFloatingCharge, fields: ["floating_charge"], requiredFields: ["floating_charge"] },
    { id: "negative_pledge", label: "7. Negative pledge", Component: Mr01PanelNegativePledge, fields: ["negative_pledge"], requiredFields: ["negative_pledge"] },
    { id: "trustee", label: "8. Trustee statement", Component: Mr01PanelTrustee, fields: ["company_is_trustee"], requiredFields: [] },
    { id: "authentication", label: "Authentication", Component: Mr01PanelAuthentication, fields: ["authenticator_name"], requiredFields: ["authenticator_name"] },
    { id: "attachment", label: "Attachment — signed deed (required)", Component: Mr01PanelAttachment, fields: ["deed_document_id"], requiredFields: ["deed_document_id"] },
  ];
}
